import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { BreadcrumbProps, Breadcrumbs } from '@tcl-boron-prefabs/breadcrumbs';

import { useAppNavigate } from '~/hooks/useAppNavigate';
import { useTrial } from '~/hooks/trials/useTrial';
import { decodePatientWorkspaceOrigin } from '~/routes/patientWorkspaceOrigin';
import { preventAnchorClick } from '~/utils/misc';

import { buildPatientWorkspaceBreadcrumbPrefix } from '../patientWorkspaceParentNav';
import { usePatientWorkspaceBreadcrumbsStyles } from './PatientWorkspaceBreadcrumbs.styles';

export const PATIENT_WORKSPACE_PATIENT_BREADCRUMB_ID = 'patient_workspace_patient';

/** Portal mount in `App` (sibling under nav); `PatientWorkspacePage` renders breadcrumbs into this element. */
export const PATIENT_WORKSPACE_BREADCRUMB_HOST_ELEMENT_ID = 'patient-workspace-breadcrumb-host' as const;

export type PatientWorkspaceBreadcrumbsProps = {
  patientName: string;
  siteId: string;
  institutionId?: string;
};

export const PatientWorkspaceBreadcrumbs: React.FC<PatientWorkspaceBreadcrumbsProps> = ({
  patientName,
  siteId,
  institutionId,
}) => {
  const classes = usePatientWorkspaceBreadcrumbsStyles();
  const navigate = useAppNavigate();
  const { search } = useLocation();

  const origin = useMemo(() => decodePatientWorkspaceOrigin(search), [search]);
  const { data: siteTrial } = useTrial(origin.kind === 'siteTrial' ? origin.trialId : undefined);
  const trialName = siteTrial?.shortName || siteTrial?.title || undefined;

  const prefix = useMemo(
    () => buildPatientWorkspaceBreadcrumbPrefix(origin, { siteId, institutionId, trialName }),
    [institutionId, origin, siteId, trialName],
  );

  const breadcrumbs: BreadcrumbProps[] = useMemo(
    () => [
      ...prefix.map((p) => ({
        text: p.text,
        id: p.id,
        href: p.href,
        ...(p.search ? { search: p.search } : {}),
      })),
      { text: patientName, id: PATIENT_WORKSPACE_PATIENT_BREADCRUMB_ID, href: '' },
    ],
    [prefix, patientName],
  );

  const handleChange = useCallback(
    (value: string) => {
      if (value === PATIENT_WORKSPACE_PATIENT_BREADCRUMB_ID) {
        return;
      }
      const selected = prefix.find((p) => p.id === value);
      if (selected) {
        navigate({
          pathname: selected.href,
          search: selected.search,
        });
      }
    },
    [navigate, prefix],
  );

  return (
    <div className={classes.breadcrumbsWrapper}>
      <div className={classes.breadcrumbsContent} onClick={preventAnchorClick}>
        <Breadcrumbs
          ariaLabel="Page navigation"
          value={PATIENT_WORKSPACE_PATIENT_BREADCRUMB_ID}
          onChange={handleChange}
          breadcrumbs={breadcrumbs}
        />
      </div>
    </div>
  );
};
